import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import each from 'lodash/each';
import find from 'lodash/find';
import get from 'lodash/get';
import includes from 'lodash/includes';
import Select2 from '../Select2';
import US_STATES from '../../constants/usStates';
import './AddressForm.css';

const propTypes = {
  address: PropTypes.object,
  onChange: PropTypes.func,
};

const defaultProps = {
  address: {},
  onChange: () => {},
};

class AddressForm extends Component {
  constructor(props) {
    super(props);
    this.addressLine1Ref = React.createRef();
  }

  componentDidMount() {
    this.autocomplete = new window.google.maps.places.Autocomplete(
      this.addressLine1Ref.current,
      { types: ['geocode'] }
    );

    this.autocomplete.addListener('place_changed', () => {
      const place = this.autocomplete.getPlace();
      if (place) {
        const addressComponents = place.address_components;
        const newAddress =
          this._buildAddressFromAddressComponents(addressComponents);
        each(newAddress, (val, key) => {
          const evt = {
            target: {
              name: `address.${key}`,
              value: val,
            },
          };
          if (key === 'region') {
            this.handleRegionChange(evt);
          } else {
            this.props.onChange(evt);
          }
        });
      }
    });
  }

  _buildAddressFromAddressComponents(addressComponents) {
    const streetNumber = this._getAddressComponent(
      addressComponents,
      'street_number'
    );
    const streetAddress = this._getAddressComponent(addressComponents, 'route');
    const city =
      this._getAddressComponent(addressComponents, 'locality') ||
      this._getAddressComponent(addressComponents, 'sublocality_level_1');
    const region = this._getAddressComponent(
      addressComponents,
      'administrative_area_level_1',
      true
    );
    const postal = this._getAddressComponent(addressComponents, 'postal_code');
    const country = this._getAddressComponent(
      addressComponents,
      'country',
      true
    );

    return {
      line1: [streetNumber, streetAddress].join(' '),
      city,
      region,
      postal,
      country,
    };
  }

  _getAddressComponent(addressComponents, type, useShortName = false) {
    const component = find(addressComponents, (component) => {
      return includes(component.types, type);
    });
    if (component) {
      return useShortName ? component.short_name : component.long_name;
    }
  }

  handleRegionChange = (e) => {
    const regionTriple = find(
      US_STATES,
      (triple) => triple[0] === e.target.value
    );
    this.props.onChange({
      target: {
        name: 'address.region',
        value: {
          code: regionTriple[0],
          name: regionTriple[1],
        },
      },
    });
  };

  get stateOptions() {
    return US_STATES.map(([code, label]) => (
      <option key={code} value={code}>
        {label}
      </option>
    ));
  }

  render() {
    const { address, onChange } = this.props;

    return (
      <Fragment>
        <div className="form-group p-t-0">
          <label htmlFor="line1" className="text-uppercase">
            Street address
          </label>
          <input
            type="text"
            className="form-control"
            id="line1"
            name="address.line1"
            value={get(address, 'line1') || ''}
            required
            aria-required="true"
            placeholder="Address Line 1"
            onChange={onChange}
            ref={this.addressLine1Ref}
          />
        </div>
        <div className="form-group p-t-0">
          <label htmlFor="line2" className="text-uppercase">
            Apartment, suite, unit, building, floor, etc.
          </label>
          <input
            type="text"
            className="form-control"
            id="line2"
            name="address.line2"
            value={get(address, 'line2') || ''}
            aria-required="true"
            placeholder="Address Line 2"
            onChange={onChange}
          />
        </div>
        <div className="form-group p-t-0">
          <label htmlFor="city" className="text-uppercase">
            City
          </label>
          <input
            type="text"
            className="form-control"
            id="city"
            name="address.city"
            value={get(address, 'city') || ''}
            required
            aria-required="true"
            onChange={onChange}
          />
        </div>
        <div className="form-group p-t-0">
          <label htmlFor="region" className="text-uppercase">
            State
          </label>
          <Select2
            className="form-control"
            id="region"
            name="address.region.code"
            value={get(address, 'region.code') || ''}
            placeholder="Select a state"
            required
            aria-required="true"
            onChange={this.handleRegionChange}
          >
            <option value="">Please select a state...</option>
            {this.stateOptions}
          </Select2>
        </div>
        <div className="form-group p-t-0">
          <label htmlFor="postal" className="text-uppercase">
            Zip code
          </label>
          <input
            type="text"
            className="form-control"
            id="postal"
            name="address.postal"
            value={get(address, 'postal') || ''}
            required
            aria-required="true"
            pattern="\d{5}"
            onChange={onChange}
          />
        </div>
      </Fragment>
    );
  }
}

AddressForm.propTypes = propTypes;
AddressForm.defaultProps = defaultProps;

export default AddressForm;
